import React, { useState, useEffect } from 'react';
import { MaterialInfo, ProductInfo, ResultInfo, AdditionalData, ProductCategory } from '../../types/ResultTypes';
import { Box, Tooltip, Link, Typography, NativeSelect, Checkbox } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { v4 as uuidv4 } from 'uuid';
import ChatWithDocuments from '../chat-with-documents/ChatWithDocuments';
import { SearchTopic, SearchTopics } from '../../enum/SearchTopics';

interface ResultsGridProps {
  results: ResultInfo[];
  additionalData: AdditionalData;
  topic: SearchTopic;
  onClickViewDetailsHandler: (data: any) => void;
  productsToCompare: string[];
  setProductsToCompare: (productsToCompare: string[]) => void;
}

interface MaterialInfoGridItem extends MaterialInfo {
  id: string;
  index: number;
  onClickViewDetails: (index: number) => void;
}

interface ProductInfoGridItem extends ProductInfo {
  id: string;
  index: number;
  onClickViewDetails: (index: number) => void;
}

interface ProductCategoryGridItem extends ProductCategory {
  id: string;
  index: number;
  onClickViewDetails: (index: number) => void;
}

type ResultInfoGridItem = MaterialInfoGridItem | ProductInfoGridItem | ProductCategoryGridItem;

const ResultsGrid: React.FC<ResultsGridProps> = ({
  results,
  additionalData,
  topic,
  onClickViewDetailsHandler,
  productsToCompare,
  setProductsToCompare,
}) => {
  const [rows, setRows] = useState<ResultInfoGridItem[]>([]);
  const [category, setCategory] = useState<ProductCategory | null | undefined>(null);

  const columnsForMaterial: GridColDef[] = [
    {
      field: 'materialId',
      headerName: 'Material Number',
      flex: 0.65,
      renderCell: (params) => (
        <ChatWithDocuments
          materialId={params.row.materialId}
          isInDistillery={params.row.isInDistillery}
          useIdForButton
        />
      ),
    },
    { field: 'title', headerName: 'Title', flex: 1 },
    { field: 'material', headerName: 'Material', flex: 0.65 },
    {
      field: 'colors',
      headerName: 'Colors',
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params?.row?.colors?.join(', ')} placement="bottom-start">
          <span>{params?.row?.colors?.join(', ')}</span>
        </Tooltip>
      ),
    },
  ];

  const categoryColumns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Category',
      flex: 1,
    },
    {
      field: 'desc',
      headerName: 'Description',
      flex: 1,
    },
    {
      field: 'pageUrl',
      headerName: 'Actions',
      flex: 1,
      renderCell: (params) => (
        <Link href={params?.row?.pageUrl} target="_blank" rel="noopener noreferrer">
          View Category on BradyId
        </Link>
      ),
    },
  ];

  const columnsForProduct: GridColDef[] = [
    {
      field: 'productId',
      headerName: 'Part Number',
      flex: 0.5,
      renderCell: (params) => (
        <Link href={'https://bradyid.com' + params?.row?.link} target="_blank" rel="noopener noreferrer">
          {params?.row?.productId}
        </Link>
      ),
    },
    { field: 'name', headerName: 'Title', flex: 1 },
    { field: 'color', headerName: 'Color', flex: 1 },
    {
      field: 'materialNumber',
      headerName: 'Material Number',
      flex: 0.5,
      renderCell: (params) => (
        <Link
          href={'https://www.bradyid.com/tdsdetails?id=' + params?.row?.materialNumber}
          target="_blank"
          rel="noopener noreferrer"
        >
          {params?.row?.materialNumber}
        </Link>
      ),
    },
    {
      field: 'toCompare',
      headerName: 'Compare',
      renderCell: (params) => (
        <Checkbox
          disabled={productsToCompare.length > 2 && !productsToCompare.includes(params?.row?.productId)}
          checked={productsToCompare.includes(params?.row?.productId)}
          onChange={(e) => {
            const checked = e.target.checked;
            const productId = params?.row?.productId;
            if (checked) {
              setProductsToCompare([...productsToCompare, productId]);
            } else {
              setProductsToCompare(productsToCompare.filter((id) => id !== productId));
            }
          }}
        />
      ),
    },
  ];

  const onClickViewDetails = (index: number) => {
    let data = results[index];
    onClickViewDetailsHandler(data);
  };

  useEffect(() => {
    if (additionalData?.productCategories.length > 0) {
      const rowsWithId = additionalData.productCategories.map((row, index) => ({
        ...row,
        id: uuidv4(),
        index: index,
        onClickViewDetails,
      }));

      setRows(rowsWithId);
    } else {
      const rowsWithId = results.map((row, index) => ({
        ...row,
        id: uuidv4(),
        index: index,
        onClickViewDetails,
      }));

      setRows(rowsWithId);
    }
  }, [results]);

  const handleRowClick = (params: any | null) => {
    if ((category === undefined || category === null) && additionalData?.productCategories.length > 0) {
      let index = params?.row?.index;
      setCategory(additionalData.productCategories[index]);
    } else {
      params?.row?.onClickViewDetails(params?.row?.index);
    }
  };

  let columns = columnsForProduct;
  if (!category && additionalData?.productCategories.length > 0) {
    columns = categoryColumns;
  } else if (topic === SearchTopics.MaterialSearch) {
    columns = columnsForMaterial;
  }

  const rowsWithId = results.map((row, index) => ({
    ...row,
    id: uuidv4(),
    index: index,
    onClickViewDetails,
  }));
  const categoriesWithIds = additionalData?.productCategories.map((category, index) => ({
    ...category,
    id: uuidv4(),
    index: index,
    onClickViewDetails,
  }));

  let gridRows = rows;
  if (!category && additionalData?.productCategories.length > 0) {
    gridRows = categoriesWithIds as ProductCategoryGridItem[];
  } else if (topic === SearchTopics.MaterialSearch) {
    gridRows = rowsWithId as MaterialInfoGridItem[];
  } else {
    gridRows = rowsWithId as ProductInfoGridItem[];
    if (category) {
      gridRows = gridRows.filter((row) => (row as ProductInfoGridItem).category === category.name);
    }
  }

  return (
    <Box
      sx={{
        marginBottom: '45px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      {topic !== SearchTopics.MaterialSearch && category && (
        <Box>
          <Box display={'inline-flex'} mb={2}>
            <Typography variant="h6" mr={2}>
              Product Category:
            </Typography>
            <NativeSelect
              defaultValue={category.ccid}
              onChange={(e) => {
                let newValue = parseInt(e.target.value);
                let productCat = categoriesWithIds.find((cat) => cat.ccid === newValue);
                setCategory(productCat);
              }}
            >
              {additionalData.productCategories.map((category, index) => (
                <option key={index} value={category.ccid}>
                  {category.title}
                </option>
              ))}
            </NativeSelect>
          </Box>
          <Box display={'inline'} whiteSpace={'nowrap'}>
            <Link
              onClick={() => {
                setCategory(null);
              }}
            >
              Show All {additionalData.productCategories.length} Categories
            </Link>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
        }}
      >
        <DataGrid
          rows={gridRows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
              },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50]}
          getRowId={() => uuidv4()}
          sx={{
            minWidth: 450,
          }}
          onRowClick={(params) => {
            console.log(params);
            handleRowClick(params);
          }}
        />
      </Box>
    </Box>
  );
};

export default ResultsGrid;
